import { useEffect, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Input } from '@/components/ui/input';
import { useI18nContext } from '@/i18n/i18n-react';
import { isValidHex, normalizeHex } from '@/lib/app-editor/color-contrast';
import { mobileLayer } from '@/lib/mobile-layer';
import { cn } from '@/lib/utils';

export interface ColorSwatch {
  name: string;
  hex: string;
}

export interface ColorFieldProps {
  label: string;
  /** A valid hex. */
  value: string;
  /** Only ever receives a valid, normalized (`#RRGGBB`) hex. */
  onChange: (hex: string) => void;
  swatches: readonly ColorSwatch[];
  otherLabel: string;
  invalidHexMessage: string;
}

/**
 * Fixed background palette of the coupon banner; the website banner adds White
 * (design §13). There is no brand colour in `TenantDto` (R16).
 */
export function useBackgroundSwatches(includeWhite = false): ColorSwatch[] {
  const { LL } = useI18nContext();
  const swatches: ColorSwatch[] = [
    { name: LL.appEditor.color.swatches.navy(), hex: '#19304D' },
    { name: LL.appEditor.color.swatches.black(), hex: '#111111' },
    { name: LL.appEditor.color.swatches.coral(), hex: '#FF6B57' },
    { name: LL.appEditor.color.swatches.green(), hex: '#1F9D55' },
    { name: LL.appEditor.color.swatches.purple(), hex: '#6D28D9' },
    { name: LL.appEditor.color.swatches.yellow(), hex: '#F5B700' },
  ];
  if (includeWhite) {
    swatches.push({
      name: LL.appEditor.color.swatches.white(),
      hex: '#FFFFFF',
    });
  }
  return swatches;
}

const OPTION_CLASS = cn(
  'h-8 w-8 rounded-full border border-border ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 aria-checked:ring-2 aria-checked:ring-primary aria-checked:ring-offset-2',
  mobileLayer.touch
);

export function ColorField({
  label,
  value,
  onChange,
  swatches,
  otherLabel,
  invalidHexMessage,
}: ColorFieldProps) {
  const normalizedValue = normalizeHex(value);
  const isSwatch = swatches.some(
    (swatch) => normalizeHex(swatch.hex) === normalizedValue
  );
  const [pickerOpen, setPickerOpen] = useState(!isSwatch);
  const [draft, setDraft] = useState(value);
  const [edited, setEdited] = useState(false);

  // Follow outside changes (a swatch, the picker) but keep what the user is
  // typing while it still means the current value.
  useEffect(() => {
    setDraft((current) =>
      normalizeHex(current) === normalizedValue ? current : value
    );
  }, [value, normalizedValue]);

  const showError = edited && !isValidHex(draft);

  const commit = (hex: string) => {
    const normalized = normalizeHex(hex);
    if (normalized) onChange(normalized);
  };

  return (
    <div className="space-y-3">
      <div
        role="radiogroup"
        aria-label={label}
        className="flex flex-wrap gap-3"
      >
        {swatches.map((swatch) => (
          <button
            key={swatch.hex}
            type="button"
            role="radio"
            aria-checked={normalizeHex(swatch.hex) === normalizedValue}
            aria-label={swatch.name}
            className={OPTION_CLASS}
            style={{ backgroundColor: swatch.hex }}
            onClick={() => {
              setEdited(false);
              onChange(swatch.hex);
            }}
          />
        ))}
        <button
          type="button"
          role="radio"
          aria-checked={!isSwatch}
          aria-label={otherLabel}
          className={OPTION_CLASS}
          style={{
            background:
              'conic-gradient(#ef4444, #eab308, #22c55e, #3b82f6, #a855f7, #ef4444)',
          }}
          onClick={() => setPickerOpen((open) => !open)}
        />
      </div>
      {pickerOpen && (
        <div className="space-y-2">
          <HexColorPicker
            color={normalizedValue ?? '#000000'}
            onChange={commit}
            className="!w-full"
          />
          <Input
            aria-label={otherLabel}
            value={draft}
            aria-invalid={showError || undefined}
            onChange={(e) => {
              setEdited(true);
              setDraft(e.target.value);
              commit(e.target.value);
            }}
          />
          {showError && (
            <p role="alert" className="text-sm text-destructive">
              {invalidHexMessage}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
